import { useCallback, useEffect, useState } from "react";
import { Building, DollarSign, Users } from "lucide-react";
import { clientService } from "@/api/clientService";
import { ClientsAccordion } from "./ClientsAccordion";
import { MetricCard } from "./MetricCard";
import { clientsApi, Summary } from "@/lib/api";
import { formatCurrency } from "@/lib/formatters";

export default function ClientsProfile() {
  const [ clients, setClients ] = useState<any[]>( [] );
  const [ searchTerm, setSearchTerm ] = useState( localStorage.getItem( "clientsProfileSearchTerm" ) || "" );
  const [ loading, setLoading ] = useState( false );
  const [ error, setError ] = useState<string | null>( null );
  const [ summaryItem, setSummaryItem ] = useState<Summary>( null );

  const [ expandedClient, setExpandedClient ] = useState<number | null>( null );
  const [ activeTab, setActiveTab ] = useState<"ver" | "gerenciar" | "historico">( "ver" );

  useEffect( () => {
    localStorage.setItem( "clientsProfileSearchTerm", searchTerm );
  }, [ searchTerm ] );

  const loadClients = useCallback( async ( search: string ) => {
    setLoading( true );
    setError( null );

    try {
      const data = await clientService.getClients( search );
      setClients( Array.isArray( data ) ? data : [] );
    } catch ( err ) {
      console.error( "❌ Erro ao buscar clientes:", err );
      // setError( "Erro ao carregar clientes." );
    } finally {
      setLoading( false );
    }
  }, [] );

  useEffect( () => {
    const handler = setTimeout( () => {
      if ( searchTerm.length >= 3 ) {
        loadClients( searchTerm );
      } else if ( searchTerm.length === 0 ) {
        loadClients( '' );
      }
    }, 500 );

    return () => clearTimeout( handler );
  }, [ searchTerm ] );

  const summary = useCallback( async () => {
    const { data } = await clientsApi.summary();
    setSummaryItem( data );
  }, [] );

  useEffect( () => {
    summary();
  }, [] );

  return (
    <main className="container mx-auto px-4 py-6">
      {/* Cabeçalho */}
      <div className="bg-white py-10 text-center mb-8 rounded-lg">
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-3 rounded-full">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-blue-600">Perfis de Cliente</h2>
          </div>
          <p className="text-muted-foreground mt-2 max-w-md">
            Visualize e gerencie os perfis dos clientes com seus produtos contratados de forma organizada.
          </p>
        </div>
        <div className="grid grid-cols-1 mt-6 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <MetricCard
            title="Total de Clientes"
            value={summaryItem?.totalClients || 0}
            badge={{
              text: 'Ativo',
              variant: 'secondary'
            }}
            color="blue"
            icon={<Building className="h-8 w-8" />}
          />

          <MetricCard
            title="Qtd. Alunos"
            value={summaryItem?.totalStudents || 0}
            badge={{
              text: 'Em Crescimento',
              variant: 'secondary'
            }}
            color="orange"
            icon={<Users className="h-8 w-8" />}
          />

          <MetricCard
            title="ACV"
            value={formatCurrency( summaryItem?.acv || 0 )}
            badge={{
              text: 'Crescimento Forte',
              variant: 'default'
            }}
            color="primary"
            icon={<DollarSign className="h-8 w-8" />}
          />
        </div>
      </div>

      {/* Campo de busca */}
      <div className="">
        <input
          type="text"
          placeholder="Pesquisar clientes, códigos, cnpj..."
          value={searchTerm}
          onChange={( e ) => setSearchTerm( e.target.value )}
          className="w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Estados */}
      {loading && <p className="text-center text-gray-500 mt-4">Carregando clientes...</p>}
      {error && <p className="text-center text-red-500 mt-4">{error}</p>}

      {/* Accordion */}
      {!loading && !error && clients.length > 0 && (
        <div className="mt-6">
          <ClientsAccordion
            clients={clients}
            expandedClient={expandedClient}
            onExpand={setExpandedClient}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      )}

      {!loading && !error && searchTerm.length >= 3 && clients.length === 0 && (
        <p className="text-center text-gray-500 mt-6">
          Nenhum cliente encontrado para "{searchTerm}".
        </p>
      )}
    </main>
  );
}
